import { ChatRuleTypeEnum, ChatRule } from "../modules/shared/model";

/**
 * Chat Rule Validation Utility
 * Validates messages against active chat rules and detects violations
 * Rules behavior changes based on contract status between participants
 * 
 * Contract Statuses that disable contact/external rules:
 * - Active (1)
 * - Accepted (7) 
 * - Completed (2)
 * - Closed (6)
 */

// Contract status enum (matching backend)
export enum ContractStatusEnum {
  Active = 1,
  Completed = 2,
  Cancelled = 3,
  Rejected = 4,
  Deleted = 5,
  Closed = 6,
  Accepted = 7
}

// Contract interface
export interface Contract {
  id: string;
  client?: { id: string } | null;
  professional?: { id: string } | null;
  status?: { id: number } | null;
  isMilestone?: boolean | null;
  milestones?: Milestone[];
}

// Milestone interface
export interface Milestone {
  id: string;
  status?: { id: number } | null;
  paymentStatus?: { id: number } | null;
}

// Milestone status enum
export enum MilestoneStatusEnum {
  Pending = 1,
  InProgress = 2,
  Completed = 3,
  Cancelled = 4
}

// Payment status enum
export enum PaymentStatusEnum {
  Pending = 1,
  Paid = 2,
  Refunded = 3,
  Failed = 4
}

// Patterns for different rule types
const PATTERNS = {
  // Email patterns
  EMAIL: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  
  // Phone number patterns (various formats)
  PHONE: /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}|\+?\d{10,15}|\d{3}[-.\s]\d{3}[-.\s]\d{4}/g,
  
  // WhatsApp/Telegram/Signal mentions
  MESSAGING_APPS: /\b(whatsapp|telegram|signal|wechat|viber|messenger|line|kik|wickr|threema|discord|slack)\b/gi,
  
  // External platform links
  EXTERNAL_LINKS: /\b(https?:\/\/(?!kokobeo\.)[\w-]+(\.[\w-]+)+[/#?]?.*?)\b/gi,
  
  // Payment platform keywords
  PAYMENT_PLATFORMS: /\b(paypal|venmo|cashapp|zelle|interac|etransfer|e-transfer|bank\s*transfer|wire\s*transfer|crypto|bitcoin|ethereum)\b/gi,
  
  // External deal keywords
  EXTERNAL_DEAL: /\b(pay\s*(?:me|you)\s*(?:via|through|on|using)|(?:message|contact|text|call|reach)\s*(?:me|us)\s*(?:on|at|via)|meet\s*(?:outside|off|away\s*from)|work\s*(?:off|outside)\s*(?:the\s*)?platform|deal\s*(?:outside|off)\s*(?:the\s*)?platform)\b/gi,
  
  // Profanity and inappropriate language (basic list - should be expanded)
  PROFANITY: /\b(fuck|shit|damn|bitch|asshole|bastard|crap|piss|dickhead|motherfucker|cunt)\b/gi,
  
  // Sensitive content keywords
  SENSITIVE_CONTENT: /\b(violence|porn|pornography|xxx|nude|naked|sex|drugs|cocaine|heroin|marijuana|weed|discrimination|racist|terrorism|bomb|weapon|gun|kill)\b/gi,
  
  // Spam indicators
  SPAM_KEYWORDS: /\b(click\s*here|limited\s*time|act\s*now|buy\s*now|free\s*money|guaranteed|100%|winner|congratulations|claim\s*your|prize)\b/gi,
};

interface ValidationResult {
  isValid: boolean;
  violatedRules: Array<{
    ruleType: ChatRuleTypeEnum;
    rule: ChatRule;
    details: string;
    matches?: string[];
  }>;
}

interface MessageData {
  message: string;
  attachments?: any[];
  senderId: string;
  roleId?: number;
  chatId?: string;
}

interface ContractContext {
  hasActiveContract: boolean;
  contract?: Contract | null;
  hasAcceptedMilestone: boolean;
}

interface UserMessageHistory {
  userId: string;
  messages: Array<{
    timestamp: number;
    message: string;
  }>;
}

// Track message history for spam detection
const userMessageHistory: Map<string, UserMessageHistory> = new Map();

/**
 * Check if there's an active contract between participants
 * @param senderId - ID of the message sender
 * @param chatParticipants - Array of chat participant IDs
 * @param contracts - Array of contracts to check
 * @returns Contract context information
 */
export function checkContractContext(
  senderId: string,
  chatParticipants: string[],
  contracts: Contract[]
): ContractContext {
  // Find contracts involving the sender and any chat participants
  const relevantContracts = contracts.filter(contract => {
    const clientId = contract.client?.id;
    const professionalId = contract.professional?.id;
    const contractStatus = Number(contract.status?.id);
    
    // Check if contract is active/accepted/completed/closed
    const isActiveContract = contractStatus === ContractStatusEnum.Active || 
                           contractStatus === ContractStatusEnum.Accepted ||
                           contractStatus === ContractStatusEnum.Completed ||
                           contractStatus === ContractStatusEnum.Closed;
    
    if (!isActiveContract) return false;
    
    // Check if sender is involved in this contract
    const senderInvolved = clientId === senderId || professionalId === senderId;
    
    // Check if any chat participant is involved
    const participantInvolved = chatParticipants.some(participantId => 
      participantId === clientId || participantId === professionalId
    );
    
    return senderInvolved && participantInvolved;
  });
  
  // Get the most recent active contract
  const activeContract = relevantContracts.length > 0 ? relevantContracts[0] : null;
  
  // Check if there are any accepted milestones
  const hasAcceptedMilestone = activeContract?.milestones?.some(milestone => {
    const milestoneStatus = Number(milestone.status?.id);
    const paymentStatus = Number(milestone.paymentStatus?.id);
    
    // Milestone is accepted if it's completed or in progress with payment
    return milestoneStatus === MilestoneStatusEnum.Completed || 
           milestoneStatus === MilestoneStatusEnum.InProgress ||
           paymentStatus === PaymentStatusEnum.Paid;
  }) || false;
  
  return {
    hasActiveContract: !!activeContract,
    contract: activeContract,
    hasAcceptedMilestone
  };
}

/**
 * Check if a rule should be applied based on contract status
 * @param ruleType - Type of rule to check
 * @param contractContext - Contract context information
 * @returns Whether the rule should be enforced
 */
function shouldEnforceRule(ruleType: number, contractContext: ContractContext): boolean {
  const { hasActiveContract, hasAcceptedMilestone } = contractContext;
  
  switch (ruleType) {
    case Number(ChatRuleTypeEnum.No_Contact_Information):
      // Disabled when contract is Active/Accepted/Completed/Closed
      return !hasActiveContract;
      
    case Number(ChatRuleTypeEnum.External_Platform_Links):
      // Always enforce external links rule (even with active contracts)
      return true;
      
    case Number(ChatRuleTypeEnum.External_Deal_Detection):
      // Disabled when contract is Active/Accepted/Completed/Closed
      return !hasActiveContract;
      
    case Number(ChatRuleTypeEnum.Professional_Language):
      // Always active
      return true;
      
    case Number(ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter):
      // Always active
      return true;
      
    case Number(ChatRuleTypeEnum.Sensitive_Content_Filter):
      // Always active
      return true;
      
    case Number(ChatRuleTypeEnum.File_Attachment_Filter):
      // Optional before contract, active after contract
      return hasActiveContract;
      
    case Number(ChatRuleTypeEnum.Chat_Message_Limit):
      // Always optional
      return false;
      
    case Number(ChatRuleTypeEnum.Role_Based_Contextual_Rules):
      // Optional before contract, active after contract
      return hasActiveContract;
      
    default:
      return true;
  }
}

/**
 * Main validation function
 * @param messageData - Message content and metadata
 * @param activeRules - List of active chat rules
 * @param contractContext - Contract context information
 * @returns Validation result with any violations
 */
export function validateMessage(
  messageData: MessageData,
  activeRules: ChatRule[],
  contractContext?: ContractContext
): ValidationResult {
  const violatedRules: ValidationResult["violatedRules"] = [];
  
  // Filter only active rules
  const activeRulesFiltered = activeRules.filter(rule => Number(rule.status.id) === 1);
  
  for (const rule of activeRulesFiltered) {
    const ruleType = Number(rule.type.id);
    
    // Check if rule should be enforced based on contract status
    if (contractContext && !shouldEnforceRule(ruleType, contractContext)) {
      continue; // Skip this rule
    }
    
    let violation: ValidationResult["violatedRules"][0] | null = null;
    
    switch (ruleType) {
      case Number(ChatRuleTypeEnum.No_Contact_Information):
        violation = validateNoContactInformation(messageData.message, rule);
        break;
        
      case Number(ChatRuleTypeEnum.Professional_Language):
        violation = validateProfessionalLanguage(messageData.message, rule);
        break;
        
      case Number(ChatRuleTypeEnum.External_Platform_Links):
        violation = validateExternalPlatformLinks(messageData.message, rule);
        break;
        
      case Number(ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter):
        violation = validateSpamAndRepetitive(messageData, rule);
        break;
        
      case Number(ChatRuleTypeEnum.Sensitive_Content_Filter):
        violation = validateSensitiveContent(messageData.message, rule);
        break;
        
      case Number(ChatRuleTypeEnum.Chat_Message_Limit):
        violation = validateMessageLimit(messageData.senderId, rule);
        break;
        
      case Number(ChatRuleTypeEnum.External_Deal_Detection):
        violation = validateExternalDeal(messageData.message, rule);
        break;
        
      case Number(ChatRuleTypeEnum.File_Attachment_Filter):
        violation = validateFileAttachments(messageData.attachments || [], rule);
        break;
        
      case Number(ChatRuleTypeEnum.Role_Based_Contextual_Rules):
        violation = validateRoleBasedRules(messageData, rule);
        break;
        
      default:
        break;
    }
    
    if (violation) {
      violatedRules.push(violation);
    }
  }
  
  return {
    isValid: violatedRules.length === 0,
    violatedRules
  };
}

/**
 * Rule 1: No Contact Information
 * Blocks messages containing phone numbers, email addresses, or other external contact details
 */
function validateNoContactInformation(
  message: string,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const matches: string[] = [];
  
  // Check for email addresses
  const emailMatches = message.match(PATTERNS.EMAIL);
  if (emailMatches) {
    matches.push(...emailMatches);
  }
  
  // Check for phone numbers
  const phoneMatches = message.match(PATTERNS.PHONE);
  if (phoneMatches) {
    matches.push(...phoneMatches);
  }
  
  // Check for messaging app mentions
  const messagingMatches = message.match(PATTERNS.MESSAGING_APPS);
  if (messagingMatches) {
    matches.push(...messagingMatches);
  }
  
  if (matches.length > 0) {
    return {
      ruleType: ChatRuleTypeEnum.No_Contact_Information,
      rule,
      details: `Message contains contact information: ${matches.join(", ")}`,
      matches
    };
  }
  
  return null;
}

/**
 * Rule 2: Professional Language
 * Flags messages containing inappropriate or unprofessional language
 */
function validateProfessionalLanguage(
  message: string,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const matches = message.match(PATTERNS.PROFANITY);
  
  if (matches) {
    return {
      ruleType: ChatRuleTypeEnum.Professional_Language,
      rule,
      details: `Message contains unprofessional language (${matches.length} violations)`,
      matches
    };
  }
  
  return null;
}

/**
 * Rule 3: External Platform Links
 * Blocks messages containing links to external platforms or services
 */
function validateExternalPlatformLinks(
  message: string,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const matches = message.match(PATTERNS.EXTERNAL_LINKS);
  
  if (matches) {
    return {
      ruleType: ChatRuleTypeEnum.External_Platform_Links,
      rule,
      details: `Message contains external links: ${matches.join(", ")}`,
      matches
    };
  }
  
  return null;
}

/**
 * Rule 4: Spam and Repetitive Messages Filter
 * Block messages sent in rapid succession or containing spam
 */
function validateSpamAndRepetitive(
  messageData: MessageData,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const now = Date.now();
  const userId = messageData.senderId;
  const message = messageData.message;
  
  // Get or create user history
  if (!userMessageHistory.has(userId)) {
    userMessageHistory.set(userId, {
      userId,
      messages: []
    });
  }
  
  const history = userMessageHistory.get(userId)!;
  
  // Remove messages older than 1 minute
  history.messages = history.messages.filter(msg => now - msg.timestamp < 60000);
  
  // Check for rapid succession (more than 5 messages in 1 minute)
  if (history.messages.length >= 5) {
    return {
      ruleType: ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter,
      rule,
      details: "Too many messages sent in rapid succession",
      matches: ["Message rate limit exceeded"]
    };
  }
  
  // Check for repeated content
  const recentSimilarMessages = history.messages.filter(
    msg => msg.message.toLowerCase() === message.toLowerCase()
  );
  
  if (recentSimilarMessages.length >= 2) {
    return {
      ruleType: ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter,
      rule,
      details: "Repetitive message content detected",
      matches: ["Duplicate message"]
    };
  }
  
  // Check for spam keywords
  const spamMatches = message.match(PATTERNS.SPAM_KEYWORDS);
  if (spamMatches && spamMatches.length >= 2) {
    return {
      ruleType: ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter,
      rule,
      details: `Message contains spam keywords: ${spamMatches.join(", ")}`,
      matches: spamMatches
    };
  }
  
  // Add to history
  history.messages.push({
    timestamp: now,
    message
  });
  
  return null;
}

/**
 * Rule 5: Sensitive Content Filter
 * Detect and block words or phrases related to prohibited content
 */
function validateSensitiveContent(
  message: string,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const matches = message.match(PATTERNS.SENSITIVE_CONTENT);
  
  if (matches) {
    return {
      ruleType: ChatRuleTypeEnum.Sensitive_Content_Filter,
      rule,
      details: `Message contains sensitive content (${matches.length} violations)`,
      matches
    };
  }
  
  return null;
}

/**
 * Rule 6: Chat Message Limit
 * Prevent flooding by limiting the number of messages sent within a defined time frame
 */
function validateMessageLimit(
  senderId: string,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const now = Date.now();
  
  if (!userMessageHistory.has(senderId)) {
    return null;
  }
  
  const history = userMessageHistory.get(senderId)!;
  
  // Check messages in last 5 minutes
  const recentMessages = history.messages.filter(
    msg => now - msg.timestamp < 300000 // 5 minutes
  );
  
  // Limit: 20 messages per 5 minutes
  if (recentMessages.length >= 20) {
    return {
      ruleType: ChatRuleTypeEnum.Chat_Message_Limit,
      rule,
      details: "Message limit exceeded (20 messages per 5 minutes)",
      matches: ["Message limit exceeded"]
    };
  }
  
  return null;
}

/**
 * Rule 7: External Deal Detection
 * Intercept messages attempting to move negotiations outside the platform
 */
function validateExternalDeal(
  message: string,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const matches: string[] = [];
  
  // Check for external deal keywords
  const externalDealMatches = message.match(PATTERNS.EXTERNAL_DEAL);
  if (externalDealMatches) {
    matches.push(...externalDealMatches);
  }
  
  // Check for payment platform mentions
  const paymentMatches = message.match(PATTERNS.PAYMENT_PLATFORMS);
  if (paymentMatches) {
    matches.push(...paymentMatches);
  }
  
  if (matches.length > 0) {
    return {
      ruleType: ChatRuleTypeEnum.External_Deal_Detection,
      rule,
      details: `Message suggests external transaction: ${matches.join(", ")}`,
      matches
    };
  }
  
  return null;
}

/**
 * Rule 8: File Attachment Filter
 * Block or restrict sending certain file types
 */
function validateFileAttachments(
  attachments: any[],
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  const blockedExtensions = [
    ".exe", ".bat", ".cmd", ".com", ".pif", ".scr", ".vbs", ".js", 
    ".jar", ".app", ".deb", ".rpm", ".dmg", ".pkg", ".sh", ".run"
  ];
  
  const suspiciousAttachments: string[] = [];
  
  for (const attachment of attachments) {
    const filename = attachment.name || attachment.filename || attachment.path || "";
    const extension = filename.toLowerCase().substring(filename.lastIndexOf("."));
    
    if (blockedExtensions.includes(extension)) {
      suspiciousAttachments.push(filename);
    }
  }
  
  if (suspiciousAttachments.length > 0) {
    return {
      ruleType: ChatRuleTypeEnum.File_Attachment_Filter,
      rule,
      details: `Blocked file types detected: ${suspiciousAttachments.join(", ")}`,
      matches: suspiciousAttachments
    };
  }
  
  return null;
}

/**
 * Rule 9: Role-Based Contextual Rules
 * Apply different rules depending on whether the message is sent by a customer or professional
 */
function validateRoleBasedRules(
  messageData: MessageData,
  rule: ChatRule
): ValidationResult["violatedRules"][0] | null {
  // This rule can be customized based on specific role requirements
  // For now, we'll apply stricter validation for certain roles
  
  const roleId = messageData.roleId;
  
  // If sender is a professional (roleId 4 or 5), apply stricter rules
  if (roleId === 4 || roleId === 5) {
    // Professionals should not request contact information aggressively
    const aggressiveContactRequests = messageData.message.match(
      /\b(give\s*me\s*your|send\s*me\s*your|share\s*your)\s*(number|phone|email|contact)\b/gi
    );
    
    if (aggressiveContactRequests) {
      return {
        ruleType: ChatRuleTypeEnum.Role_Based_Contextual_Rules,
        rule,
        details: "Professional requesting contact information inappropriately",
        matches: aggressiveContactRequests
      };
    }
  }
  
  return null;
}

/**
 * Clear message history for a specific user (useful for cleanup)
 */
export function clearUserMessageHistory(userId: string): void {
  userMessageHistory.delete(userId);
}

/**
 * Clear all message history (useful for maintenance)
 */
export function clearAllMessageHistory(): void {
  userMessageHistory.clear();
}

